import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Laptop, Smartphone, LogOut } from "lucide-react";
import { api, errMsg, fmtDateTime } from "../lib/api";

const ago = (s) => {
  const m = Math.max(0, Math.round((Date.now() - new Date(s).getTime()) / 60000));
  if (m < 5) return "Active now";
  if (m < 60) return `Last active ${m} minutes ago`;
  if (m < 1440) return `Last active ${Math.round(m / 60)} hour${m < 90 ? "" : "s"} ago`;
  const d = Math.round(m / 1440);
  return d < 30 ? `Last active ${d} day${d > 1 ? "s" : ""} ago` : `Last active ${fmtDateTime(s)}`;
};

const DeviceRow = ({ d, onOut }) => {
  const Icon = /iPhone|iPad|Android/.test(d.device) ? Smartphone : Laptop;
  return (
    <li className="flex items-center gap-3 py-3" data-testid={`device-row-${d.key}`}>
      <Icon className="w-5 h-5 text-slate-500 shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold truncate">{d.device}{d.current && <span className="ml-2 text-xs font-medium text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full" data-testid="device-current-badge">This device</span>}</p>
        <p className="text-xs muted">{d.current ? "Active now" : ago(d.last_seen)}{d.ip && ` · ${d.ip}`}</p>
      </div>
      {!d.current && <button onClick={() => onOut(d)} className="btn-ghost text-sm" data-testid={`device-signout-${d.key}`}>Sign out</button>}
    </li>
  );
};

export const SignedInDevices = () => {
  const [list, setList] = useState(null);
  const load = useCallback(() => api.get("/account/devices").then((r) => setList(r.data)).catch(() => setList([])), []);
  useEffect(() => { load(); }, [load]);
  const out = async (d) => {
    try { await api.delete(`/account/devices/${d.key}`); toast.success(`Signed out ${d.device}`); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const outAll = async () => {
    if (!window.confirm("Sign out every other device? You'll stay signed in here.")) return;
    try { await api.post("/account/devices/sign-out-others"); toast.success("Signed out all other devices"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const others = (list || []).filter((d) => !d.current).length;
  return (
    <section className="card p-6" data-testid="signed-in-devices">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-bold">Signed-in devices</h2>
        {others > 0 && <button onClick={outAll} className="btn bg-rose-50 text-rose-700 hover:bg-rose-100 text-sm flex items-center gap-1.5" data-testid="devices-signout-others-btn"><LogOut className="w-4 h-4" />Sign out all other devices</button>}
      </div>
      <p className="text-sm muted mt-1">If you don't recognise a device, sign it out and change your password.</p>
      {list === null ? <p className="text-sm muted mt-4">Loading...</p> : list.length === 0 ? <p className="text-sm muted mt-4" data-testid="devices-empty">No devices yet. They'll appear here the next time you sign in.</p> : (
        <ul className="divide-y divide-slate-100 mt-2" data-testid="devices-list">{list.map((d) => <DeviceRow key={d.key} d={d} onOut={out} />)}</ul>
      )}
    </section>
  );
};
